const ACCESS_MODES = Object.freeze({
  FULL: "full",
  GRACE: "grace",
  LANDING_ONLY: "landing_only",
  SUSPENDED: "suspended",
});

function validDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function daysUntil(value, now = new Date()) {
  const date = validDate(value);
  if (!date) return null;
  return Math.max(0, Math.ceil((date.getTime() - now.getTime()) / 86400000));
}

export function getProjectBillingAccess(project, now = new Date()) {
  const billing = project?.billing || {};
  const billingStatus = String(billing.status || "trial").toLowerCase();
  const projectStatus = String(project?.status || "active").toLowerCase();
  const explicitMode = String(billing.accessMode || "").toLowerCase();
  const graceUntil = validDate(billing.graceUntil);
  const trialStartedAt = validDate(billing.trialStartedAt);
  const trialEndsAt = validDate(billing.trialEndsAt);
  const trialExpired = billingStatus === "trial" && trialEndsAt && now > trialEndsAt;

  let mode = ACCESS_MODES.FULL;
  let reason = billingStatus === "trial" ? "trial_active" : "billing_active";

  if (projectStatus === "cancelled") {
    mode = ACCESS_MODES.SUSPENDED;
    reason = "project_cancelled";
  } else if (projectStatus === "paused" && billing.pausedByBilling !== true) {
    mode = ACCESS_MODES.SUSPENDED;
    reason = "project_paused_by_admin";
  } else if (trialExpired) {
    mode = ACCESS_MODES.LANDING_ONLY;
    reason = "trial_expired";
  } else if (billingStatus === "past_due") {
    if (graceUntil && now <= graceUntil) {
      mode = ACCESS_MODES.GRACE;
      reason = "payment_grace_period";
    } else {
      mode = ACCESS_MODES.LANDING_ONLY;
      reason = "payment_grace_expired";
    }
  } else if (["paused", "cancelled"].includes(billingStatus)) {
    mode = ACCESS_MODES.LANDING_ONLY;
    reason = billingStatus === "cancelled"
      ? "subscription_cancelled"
      : "subscription_paused";
  } else if (explicitMode === ACCESS_MODES.LANDING_ONLY) {
    mode = ACCESS_MODES.LANDING_ONLY;
    reason = "billing_restricted";
  } else if (explicitMode === ACCESS_MODES.GRACE) {
    mode = ACCESS_MODES.GRACE;
    reason = "payment_grace_period";
  } else if (explicitMode === ACCESS_MODES.SUSPENDED) {
    mode = ACCESS_MODES.SUSPENDED;
    reason = "billing_suspended";
  }

  return {
    mode,
    reason,
    billingStatus,
    projectStatus,
    graceUntil,
    trialStartedAt,
    trialEndsAt,
    trialDaysRemaining: billingStatus === "trial" ? daysUntil(trialEndsAt, now) : null,
    trialExpired: Boolean(trialExpired),
    restrictedAt: validDate(billing.restrictedAt),
    daysRemaining: daysUntil(graceUntil, now),
    isTrial: billingStatus === "trial",
    isFull: mode === ACCESS_MODES.FULL,
    isGrace: mode === ACCESS_MODES.GRACE,
    isLandingOnly: mode === ACCESS_MODES.LANDING_ONLY,
    isSuspended: mode === ACCESS_MODES.SUSPENDED,
    isOperational: [ACCESS_MODES.FULL, ACCESS_MODES.GRACE].includes(mode),
    landingAvailable: mode !== ACCESS_MODES.SUSPENDED,
    isPaymentRestricted: [ACCESS_MODES.GRACE, ACCESS_MODES.LANDING_ONLY].includes(mode)
      || reason === "billing_suspended",
    needsPaymentAction: mode === ACCESS_MODES.GRACE
      || mode === ACCESS_MODES.LANDING_ONLY
      || reason === "billing_suspended",
  };
}

export function billingAccessLabel(access) {
  if (access?.isTrial && !access?.trialExpired) {
    const days = access.trialDaysRemaining;
    return days === 1 ? "Prueba gratis · 1 día" : `Prueba gratis · ${days ?? 30} días`;
  }
  if (access?.reason === "trial_expired") return "Prueba finalizada · solo landing";
  if (access?.isGrace) return "Pago pendiente · período de gracia";
  if (access?.reason === "subscription_paused") return "Suscripción pausada · solo landing";
  if (access?.reason === "subscription_cancelled") return "Suscripción finalizada · solo landing";
  if (access?.isLandingOnly) return "Funciones pausadas · solo landing";
  if (access?.reason === "billing_suspended") return "Suspendido por pago";
  if (access?.isSuspended) return "Proyecto suspendido";
  return "Servicio activo";
}

export function billingAccessLockLabel(access) {
  if (!access) return "temporalmente bloqueado";
  if (access.reason === "trial_expired") return "prueba finalizada";
  if (access.reason === "subscription_cancelled") return "suscripción finalizada";
  if (access.reason === "subscription_paused") return "suscripción pausada";
  if (access.isLandingOnly) return "pausado hasta regularizar el pago";
  if (access.reason === "billing_suspended") return "suspendido por pago";
  if (access.isSuspended) return "proyecto suspendido";
  return "temporalmente bloqueado";
}

export function billingAccessActionLabel(access) {
  if (access?.reason === "trial_expired") return "Elegir suscripción";
  if (access?.isGrace) return "Resolver pago";
  if (access?.isLandingOnly) return "Reactivar funciones";
  if (access?.reason === "billing_suspended") return "Resolver pago";
  if (access?.isSuspended) return "Ver configuración";
  if (access?.isTrial) return "Ver continuidad";
  return "Ver plan";
}

export function billingNavigationStatus(access) {
  if (!access) return null;

  if (access.isTrial && !access.trialExpired) {
    const days = access.trialDaysRemaining;
    return {
      key: "trial",
      label: days === 1 ? "Prueba gratis · 1 día" : `Prueba gratis · ${days ?? 30} días`,
      compactLabel: days === 1 ? "PRUEBA · 1D" : `PRUEBA · ${days ?? 30}D`,
      tone: "success",
      actionTarget: "billing",
      actionLabel: billingAccessActionLabel(access),
    };
  }

  if (access.isGrace) {
    return {
      key: "grace",
      label: "Pago pendiente · período de gracia",
      compactLabel: "PAGO PENDIENTE",
      tone: "warning",
      actionTarget: "billing",
      actionLabel: billingAccessActionLabel(access),
    };
  }

  if (access.isLandingOnly) {
    return {
      key: "landing-only",
      label: billingAccessLabel(access),
      compactLabel: "SOLO LANDING",
      tone: "error",
      actionTarget: "billing",
      actionLabel: billingAccessActionLabel(access),
    };
  }

  if (access.isSuspended) {
    const paymentSuspended = access.reason === "billing_suspended";
    return {
      key: paymentSuspended ? "billing-suspended" : "suspended",
      label: paymentSuspended ? "Suspendido por pago" : "Proyecto suspendido",
      compactLabel: paymentSuspended ? "PAGO REQUERIDO" : "SUSPENDIDO",
      tone: paymentSuspended ? "error" : "default",
      actionTarget: paymentSuspended ? "billing" : "settings",
      actionLabel: billingAccessActionLabel(access),
    };
  }

  return null;
}

export { ACCESS_MODES };
